import { useState, useEffect, useCallback } from 'react';
import eodhd from '../services/eodhd';
import api, { type ScreenerSignal, type SignalResponse, type SignalType } from '../services/api';
import { getCached, setCache, CACHE_TTL } from '../services/cache';
import type { RealTimePrice } from '../types';
import { ETFS, FOREX_PAIRS, COMMODITIES } from '../utils/constants';

interface UseMarketDataOptions {
  ttl?: number;
  refreshInterval?: number;
  enabled?: boolean;
}

interface UseMarketDataResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching real-time price with caching
 */
export function useRealTimePrice(
  symbol: string,
  options: UseMarketDataOptions = {}
): UseMarketDataResult<RealTimePrice> {
  const { ttl = CACHE_TTL.REAL_TIME, refreshInterval, enabled = true } = options;
  const [data, setData] = useState<RealTimePrice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!symbol || !enabled) return;

    const cacheKey = `price_${symbol}`;

    // Check cache first
    const cached = getCached<RealTimePrice>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      const result = await eodhd.getRealTimePrice(symbol);
      setCache(cacheKey, result, ttl);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch price'));
    } finally {
      setLoading(false);
    }
  }, [symbol, ttl, enabled]);

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Set up refresh interval if specified
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshInterval]);

  return { data, loading, error, refresh: fetchData };
}

/**
 * Hook for fetching multiple prices with bulk API and caching
 */
export function useBulkPrices(
  symbols: string[],
  options: UseMarketDataOptions = {}
): UseMarketDataResult<Map<string, RealTimePrice>> {
  const { ttl = CACHE_TTL.REAL_TIME, refreshInterval, enabled = true } = options;
  const [data, setData] = useState<Map<string, RealTimePrice> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!symbols.length || !enabled) return;

    const results = new Map<string, RealTimePrice>();
    const uncached: string[] = [];

    // Check cache for each symbol
    for (const symbol of symbols) {
      const cacheKey = `price_${symbol}`;
      const cached = getCached<RealTimePrice>(cacheKey);
      if (cached) {
        results.set(symbol, cached);
      } else {
        uncached.push(symbol);
      }
    }

    // If all cached, return immediately
    if (uncached.length === 0) {
      setData(results);
      setLoading(false);
      return;
    }

    try {
      // Fetch uncached symbols individually (bulk API has limitations)
      const promises = uncached.map(async (symbol) => {
        try {
          const result = await eodhd.getRealTimePrice(symbol);
          setCache(`price_${symbol}`, result, ttl);
          return { symbol, data: result };
        } catch {
          return { symbol, data: null };
        }
      });

      const fetched = await Promise.all(promises);
      for (const { symbol, data: priceData } of fetched) {
        if (priceData) {
          results.set(symbol, priceData);
        }
      }

      setData(results);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch prices'));
    } finally {
      setLoading(false);
    }
  }, [symbols, ttl, enabled]);

  useEffect(() => {
    fetchData();

    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshInterval]);

  return { data, loading, error, refresh: fetchData };
}

interface MarketPulseItem {
  symbol: string;
  label: string;
  value: string;
  change: number;
}

/**
 * Hook specifically for market pulse data
 */
export function useMarketPulse(options: UseMarketDataOptions = {}): UseMarketDataResult<MarketPulseItem[]> {
  const { ttl = CACHE_TTL.MARKET_DATA, enabled = true } = options;
  const [data, setData] = useState<MarketPulseItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const MARKET_PULSE_SYMBOLS = [
    { symbol: 'CTOP50.JSE', label: 'JSE Top 50' },
    { symbol: 'GSPC.INDX', label: 'S&P 500' },
    { symbol: 'IXIC.INDX', label: 'NASDAQ' },
    { symbol: 'BTC-USD.CC', label: 'BTC/USD' },
    { symbol: 'ETH-USD.CC', label: 'ETH/USD' },
    { symbol: 'USDZAR.FOREX', label: 'USD/ZAR' },
  ];

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const cacheKey = 'market_pulse';
    const cached = getCached<MarketPulseItem[]>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      const promises = MARKET_PULSE_SYMBOLS.map(async (item) => {
        try {
          const priceData = await eodhd.getRealTimePrice(item.symbol);
          const isCrypto = item.label.includes('BTC') || item.label.includes('ETH');
          const isCurrency = item.label.includes('/');
          const value = priceData.close || 0;

          let formattedValue: string;
          if (isCrypto) {
            formattedValue = `$${value.toLocaleString()}`;
          } else if (isCurrency) {
            formattedValue = value.toFixed(2);
          } else {
            formattedValue = value.toLocaleString();
          }

          return {
            symbol: item.symbol,
            label: item.label,
            value: formattedValue,
            change: priceData.change_percent ?? priceData.change_p ?? 0,
          };
        } catch {
          return {
            symbol: item.symbol,
            label: item.label,
            value: '--',
            change: 0,
          };
        }
      });

      const results = await Promise.all(promises);
      setCache(cacheKey, results, ttl);
      setData(results);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch market pulse'));
    } finally {
      setLoading(false);
    }
  }, [ttl, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

type AssetType = 'stock' | 'crypto' | 'etf' | 'forex';

interface HotMover {
  symbol: string;
  name: string;
  change: number;
  type: AssetType;
}

interface HotMoversData {
  gainers: HotMover[];
  losers: HotMover[];
}

/**
 * Hook for hot movers data
 */
export function useHotMovers(options: UseMarketDataOptions = {}): UseMarketDataResult<HotMoversData> {
  const { ttl = CACHE_TTL.HOT_MOVERS, enabled = true } = options;
  const [data, setData] = useState<HotMoversData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const cacheKey = 'hot_movers';
    const cached = getCached<HotMoversData>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      // Fetch from dynamic backend endpoint
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/v1/market/movers`);

      if (!response.ok) {
        throw new Error('Failed to fetch hot movers');
      }

      const result = await response.json();

      // Transform backend response to frontend format
      const transformMover = (mover: any): HotMover => ({
        symbol: mover.symbol, // Keep full symbol with exchange suffix
        name: mover.name,
        change: mover.change_percent,
        type: mover.type as AssetType,
      });

      const hotMoversData: HotMoversData = {
        gainers: result.gainers.map(transformMover),
        losers: result.losers.map(transformMover),
      };

      setCache(cacheKey, hotMoversData, ttl);
      setData(hotMoversData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch hot movers'));
    } finally {
      setLoading(false);
    }
  }, [ttl, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

interface WatchlistItem {
  symbol: string;
  name: string;
  price: number;
  change: number;
  previousClose?: number;
  alerts: number;
  exchange: string;
  currency: string;
}

/**
 * Hook for watchlist data - fetches from backend API
 */
export function useWatchlist(options: UseMarketDataOptions = {}): UseMarketDataResult<WatchlistItem[]> {
  const { ttl = CACHE_TTL.REAL_TIME, enabled = true } = options;
  const [data, setData] = useState<WatchlistItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const cacheKey = 'watchlist';
    const cached = getCached<WatchlistItem[]>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      // Fetch user's watchlists from backend
      const watchlists = await api.getWatchlists();

      // If no watchlists exist, return empty array
      if (!watchlists || watchlists.length === 0) {
        setData([]);
        setLoading(false);
        return;
      }

      // Use first watchlist for Dashboard preview
      const firstWatchlist = watchlists[0];

      // Fetch real-time prices for each item in the watchlist
      const promises = firstWatchlist.items.map(async (item) => {
        try {
          const priceData = await eodhd.getRealTimePrice(item.symbol);

          // Determine currency from symbol
          const currency = item.symbol.includes('JSE') ? 'R' : '$';

          return {
            symbol: item.symbol, // Keep full symbol for navigation
            name: item.name,
            price: priceData.close || 0,
            change: priceData.change_percent ?? priceData.change_p ?? 0,
            previousClose: priceData.previous_close || priceData.previousClose,
            alerts: 0, // TODO: Fetch real alert counts when alerts system is integrated
            exchange: item.exchange,
            currency,
          };
        } catch {
          // If price fetch fails, return basic data
          const currency = item.symbol.includes('JSE') ? 'R' : '$';
          return {
            symbol: item.symbol, // Keep full symbol for navigation
            name: item.name,
            price: 0,
            change: 0,
            previousClose: undefined,
            alerts: 0,
            exchange: item.exchange,
            currency,
          };
        }
      });

      const results = await Promise.all(promises);
      setCache(cacheKey, results, ttl);
      setData(results);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch watchlist'));
      setData([]); // Return empty array on error
    } finally {
      setLoading(false);
    }
  }, [ttl, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

interface UseSignalsOptions {
  ttl?: number;
  enabled?: boolean;
  market?: 'jse' | 'us' | 'all';
  filterType?: SignalType;
  minConfidence?: number;
}

interface SignalSummary {
  buyCount: number;
  holdCount: number;
  sellCount: number;
}

interface UseSignalsResult {
  data: ScreenerSignal[] | null;
  summary: SignalSummary;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching screener signals from the backend
 */
export function useScreenerSignals(options: UseSignalsOptions = {}): UseSignalsResult {
  const { ttl = CACHE_TTL.HOT_MOVERS, enabled = true, market = 'all', filterType, minConfidence = 0 } = options;
  const [data, setData] = useState<ScreenerSignal[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const cacheKey = `screener_signals_${market}_${filterType || 'all'}_${minConfidence}`;
    const cached = getCached<ScreenerSignal[]>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      // Prepare all API calls to run in parallel
      const apiCalls: Promise<ScreenerSignal[]>[] = [];

      if (market === 'jse' || market === 'all') {
        apiCalls.push(
          api.getJSEScreenerSignals(filterType, minConfidence).then(res => res.signals)
        );
      }

      if (market === 'us' || market === 'all') {
        apiCalls.push(
          api.getUSScreenerSignals(filterType, minConfidence).then(res => res.signals)
        );
      }

      // Fetch signals for ETFs, Forex, and Commodities
      if (market === 'all') {
        // ETFs - top 5 most liquid
        const etfSymbols = ETFS.map(etf => etf.symbol).slice(0, 5);
        apiCalls.push(
          api.getCustomScreenerSignals(etfSymbols, filterType, minConfidence).then(res => res.signals)
        );

        // Forex - major pairs only (first 5)
        const forexSymbols = FOREX_PAIRS.slice(0, 5).map(pair => pair.symbol);
        apiCalls.push(
          api.getCustomScreenerSignals(forexSymbols, filterType, minConfidence).then(res => res.signals)
        );

        // Commodities - include all spot prices (XAUUSD, XAGUSD, XPTUSD) plus top ETFs
        // Filter for spot prices (.FOREX) and top 3 ETFs
        const commoditySymbols = COMMODITIES
          .filter(c => c.symbol.includes('.FOREX') || COMMODITIES.indexOf(c) < 3)
          .map(comm => comm.symbol);
        apiCalls.push(
          api.getCustomScreenerSignals(commoditySymbols, filterType, minConfidence).then(res => res.signals)
        );
      }

      // Execute all API calls in parallel and flatten results
      const results = await Promise.all(apiCalls);
      const signals = results.flat();

      // Sort: strategy_match=true first, then by confidence descending
      signals.sort((a, b) => {
        const aMatch = a.strategy_match ? 1 : 0;
        const bMatch = b.strategy_match ? 1 : 0;
        if (bMatch !== aMatch) return bMatch - aMatch;
        return b.confidence - a.confidence;
      });

      setCache(cacheKey, signals, ttl);
      setData(signals);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch signals'));
    } finally {
      setLoading(false);
    }
  }, [ttl, enabled, market, filterType, minConfidence]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary: SignalSummary = {
    buyCount: data?.filter(s => s.signal === 'buy' || s.signal === 'strong_buy').length || 0,
    holdCount: data?.filter(s => s.signal === 'neutral' || s.signal === 'hold').length || 0,
    sellCount: data?.filter(s => s.signal === 'sell' || s.signal === 'strong_sell').length || 0,
  };

  return { data, summary, loading, error, refresh: fetchData };
}

interface UseSignalResult {
  data: SignalResponse | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching a single symbol's signal
 */
export function useSignal(symbol: string, options: { ttl?: number; enabled?: boolean } = {}): UseSignalResult {
  const { ttl = CACHE_TTL.MARKET_DATA, enabled = true } = options;
  const [data, setData] = useState<SignalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!symbol || !enabled) return;

    const cacheKey = `signal_${symbol}`;
    const cached = getCached<SignalResponse>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    try {
      const response = await api.getSignal(symbol);
      setCache(cacheKey, response, ttl);
      setData(response);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch signal'));
    } finally {
      setLoading(false);
    }
  }, [symbol, ttl, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}
